import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import NavItem, { SidebarItem } from './NavItem';

export type { SidebarItem } from './NavItem';

const STORAGE_KEY = 'CS_ADMIN_SIDEBAR_COLLAPSED';
const MOBILE_QUERY = '(max-width: 767px)';

type SidebarProps = {
  items: SidebarItem[];
  routeState?: unknown;
};

const readCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const ExistingShopSidebar = ({ items, routeState }: SidebarProps) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const location = useLocation();
  const railCollapsed = mobile || collapsed;

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const update = () => {
      setMobile(media.matches);
      setDrawerOpen(false);
    };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  useEffect(() => {
    if (!drawerOpen) return;
    const dialog = dialogRef.current;
    const toggleButton = toggleRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog?.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog?.close();
      document.body.style.overflow = previousOverflow;
      toggleButton?.focus();
    };
  }, [drawerOpen]);

  const toggle = () => {
    if (mobile) {
      setDrawerOpen(true);
    } else {
      const nextCollapsed = !collapsed;
      setCollapsed(nextCollapsed);
      try {
        localStorage.setItem(STORAGE_KEY, String(nextCollapsed));
      } catch {}
    }
  };

  const renderItems = (isCollapsed: boolean) => (
    <>
      <RoutesNav aria-label='Shop settings'>
        {items.filter((item) => item.type === 'route').map((item) => (
          <NavItem key={item.id} item={item} collapsed={isCollapsed} routeState={routeState} onSelect={() => setDrawerOpen(false)} />
        ))}
      </RoutesNav>
      <Actions>
        {items.filter((item) => item.type === 'action').map((item) => (
          <NavItem key={item.id} item={item} collapsed={isCollapsed} onSelect={() => setDrawerOpen(false)} />
        ))}
      </Actions>
    </>
  );

  const toggleButton = (
    <Toggle ref={toggleRef} type='button' aria-label={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!railCollapsed || drawerOpen} aria-controls={mobile ? 'shop-sidebar-drawer' : 'shop-sidebar-menu'} onClick={toggle}>
      {railCollapsed ? <BiChevronRight aria-hidden='true' /> : <BiChevronLeft aria-hidden='true' />}
      {!railCollapsed && <span>Collapse</span>}
    </Toggle>
  );

  return (
    <SidebarSlot $collapsed={railCollapsed}>
      <Rail $hidden={drawerOpen} aria-hidden={drawerOpen || undefined}>
        {railCollapsed ? (
          <Tooltip title='Expand sidebar' placement='right' trigger={['hover', 'focus']} styles={{ root: { pointerEvents: 'none' } }}>
            {toggleButton}
          </Tooltip>
        ) : toggleButton}
        <Menu id='shop-sidebar-menu'>{renderItems(railCollapsed)}</Menu>
      </Rail>
      {mobile && (
        <Drawer ref={dialogRef} id='shop-sidebar-drawer' aria-label='Shop navigation' aria-modal='true'
          onCancel={(event) => { event.preventDefault(); setDrawerOpen(false); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setDrawerOpen(false);
            }
          }}
          onClose={() => setDrawerOpen(false)}
          onClick={(event) => { if (event.target === event.currentTarget) setDrawerOpen(false); }}>
          <DrawerPanel>
            <Toggle type='button' aria-label='Collapse sidebar' aria-expanded='true' aria-controls='shop-sidebar-drawer'
              autoFocus onClick={() => setDrawerOpen(false)}>
              <BiChevronLeft aria-hidden='true' /><span>Collapse</span>
            </Toggle>
            <Menu>{renderItems(false)}</Menu>
          </DrawerPanel>
        </Drawer>
      )}
    </SidebarSlot>
  );
};

export default ExistingShopSidebar;

const SidebarSlot = styled.aside<{ $collapsed: boolean }>`
  box-sizing: border-box;
  flex: 0 0 ${({ $collapsed }) => $collapsed ? '64px' : '240px'};
  width: ${({ $collapsed }) => $collapsed ? '64px' : '240px'};
  align-self: stretch;
  background: #fff;
  border-right: 1px solid #dce1e5;
  transition: width 180ms ease, flex-basis 180ms ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const Rail = styled.div<{ $hidden: boolean }>`
  position: sticky;
  top: 0;
  max-height: 100vh;
  max-height: 100dvh;
  overflow-y: auto;
  padding: 8px 4px;
  visibility: ${({ $hidden }) => $hidden ? 'hidden' : 'visible'};
`;
const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 10px 15px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #39424a;
  font: inherit;
  cursor: pointer;
  svg { flex: 0 0 20px; width: 20px; height: 20px; }
  &:hover { background: #f1f3f5; }
  &:focus-visible { outline: 2px solid #557dec; outline-offset: -2px; }
`;
const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
`;
const RoutesNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Actions = styled.div`
  border-top: 1px solid #dce1e5;
  padding-top: 12px;
`;
const Drawer = styled.dialog`
  position: fixed;
  inset: 0;
  width: 100%;
  max-width: none;
  height: 100vh;
  height: 100dvh;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  &::backdrop { background: #20272d66; }
`;
const DrawerPanel = styled.div`
  box-sizing: border-box;
  width: min(240px, 100%);
  height: 100%;
  padding: 8px 4px;
  overflow-y: auto;
  background: #fff;
  border-right: 1px solid #dce1e5;
`;